/**
 * Main application router component
 * 
 * Handles routing between projects list, project detail, templates,
 * document spaces, and about pages
 */

import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';

// Lazy load main route components for better performance
const ProjectsList = React.lazy(() => 
  import('./projects/ProjectsList').then(module => ({ default: module.ProjectsList }))
);
const ProjectDetail = React.lazy(() => 
  import('./projects/ProjectDetail').then(module => ({ default: module.ProjectDetail }))
);
const TemplatesPage = React.lazy(() => 
  import('./templates/TemplatesPage').then(module => ({ default: module.TemplatesPage }))
);
const SpacesList = React.lazy(() => 
  import('./spaces/SpacesList').then(module => ({ default: module.SpacesList }))
);
const SpaceDetail = React.lazy(() => 
  import('./spaces/SpaceDetail').then(module => ({ default: module.SpaceDetail }))
);
const AboutPage = React.lazy(() => 
  import('./about/AboutPage').then(module => ({ default: module.AboutPage }))
);
const NotFound = React.lazy(() => 
  import('./common/NotFound').then(module => ({ default: module.NotFound }))
);

// Loading component for lazy-loaded routes
const RouteLoader: React.FC = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="200px"
  >
    <CircularProgress />
  </Box>
);

/**
 * Main application router component
 * 
 * Route Structure:
 * / → Redirect to /projects
 * /projects → Projects list page
 * /projects/:id → Individual project dashboard
 * /templates → Templates management page
 * /spaces → Document Spaces list page
 * /spaces/:spaceId → Individual space detail
 * * → 404 Not Found page
 */
export const AppRouter: React.FC = () => {
  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        {/* Root redirect to projects */}
        <Route path="/" element={<Navigate to="/projects" replace />} />
        
        {/* Main application routes with lazy loading */}
        <Route path="/projects" element={<ProjectsList />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/templates/:templateId" element={<TemplatesPage />} />
        <Route path="/spaces" element={<SpacesList />} />
        <Route path="/spaces/:spaceId" element={<SpaceDetail />} />
        <Route path="/about" element={<AboutPage />} />
        
        {/* 404 catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
